import { useState, useRef, useEffect } from "react";
import LogoIcon from "../../assets/icons/logo.png";
import ChatBubbleIcon from "../../assets/icons/message_logo.png";
import ChevronRightIcon from "../../assets/icons/chat_history_back.png";
import sidebar from "../../assets/icons/sidebar.png";

function Sidebar({ chats, chatTitles = {}, activeChatId, onSelectChat, onDeleteChat, onChatHistoryClick, onCloseSidebar, collapsed, isOpen }) {
  const chatIds = Object.keys(chats);
  const hasRecentChats = chatIds.length > 0;
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  // Pinned chats persisted in localStorage
  const [pinnedChats, setPinnedChats] = useState(() => {
    try {
      const raw = localStorage.getItem("pinnedChats");
      return raw ? JSON.parse(raw) : [];
    } catch (err) {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("pinnedChats", JSON.stringify(pinnedChats));
    } catch (err) {
      // ignore
    }
  }, [pinnedChats]);

  const handleMenuClick = (e, chatId) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === chatId ? null : chatId);
  };

  const handleDelete = (e, chatId) => {
    e.stopPropagation();
    setOpenMenuId(null);
    // Remove from pinned list if present
    setPinnedChats((prev) => prev.filter((id) => id !== chatId));
    if (onDeleteChat) {
      onDeleteChat(chatId);
    }
  };

  const handlePinToggle = (e, chatId) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setPinnedChats((prev) => {
      const exists = prev.includes(chatId);
      if (exists) return prev.filter((id) => id !== chatId);
      return [chatId, ...prev];
    });
  };

  const getChatTitle = (chatId) => {
    // Prefer server / stored auto-title (ChatGPT-style)
    if (chatTitles[chatId] && chatTitles[chatId] !== "New Chat") {
      return chatTitles[chatId];
    }

    const msgs = chats[chatId] || [];

    // Greetings to skip
    const greetings = ["hi", "hello", "hey", "hii", "hiii", "good morning", "good afternoon", "good evening", "health concern", "fitness & lifestyle", "doctor consultation"];

    // Specific topics to detect (keyword -> Display Title)
    const specificTopics = [
      // Pain types
      { keywords: ["back pain", "backache", "lower back"], title: "Back Pain" },
      { keywords: ["headache", "head pain", "migraine"], title: "Headache" },
      { keywords: ["chest pain", "chest tightness"], title: "Chest Pain" },
      { keywords: ["stomach pain", "abdominal pain", "stomach ache"], title: "Stomach Pain" },
      { keywords: ["knee pain", "knee problem"], title: "Knee Pain" },
      { keywords: ["shoulder pain"], title: "Shoulder Pain" },
      { keywords: ["neck pain"], title: "Neck Pain" },
      { keywords: ["joint pain", "joints"], title: "Joint Pain" },
      { keywords: ["tooth pain", "toothache", "dental"], title: "Tooth Pain" },
      { keywords: ["eye pain", "eyes hurt", "vision problem", "blurred vision"], title: "Eye Problem" },
      { keywords: ["ear pain", "ear infection"], title: "Ear Pain" },

      // Health conditions
      { keywords: ["fever", "high temperature"], title: "Fever" },
      { keywords: ["cold", "runny nose", "sneezing"], title: "Cold" },
      { keywords: ["cough", "coughing"], title: "Cough" },
      { keywords: ["sore throat", "throat pain"], title: "Sore Throat" },
      { keywords: ["acidity", "heartburn", "acid reflux"], title: "Acidity" },
      { keywords: ["constipation"], title: "Constipation" },
      { keywords: ["diarrhea", "loose motion"], title: "Diarrhea" },
      { keywords: ["nausea", "vomiting"], title: "Nausea" },
      { keywords: ["allergy", "allergic"], title: "Allergy" },
      { keywords: ["skin rash", "rash", "itching", "skin problem"], title: "Skin Problem" },
      { keywords: ["hair loss", "hair fall"], title: "Hair Loss" },
      { keywords: ["acne", "pimple"], title: "Acne" },
      { keywords: ["diabetes", "blood sugar"], title: "Diabetes" },
      { keywords: ["blood pressure", "bp", "hypertension"], title: "Blood Pressure" },
      { keywords: ["thyroid"], title: "Thyroid" },

      // Mental health
      { keywords: ["stress", "stressed"], title: "Stress" },
      { keywords: ["anxiety", "anxious", "panic"], title: "Anxiety" },
      { keywords: ["depression", "depressed", "sad"], title: "Depression" },
      { keywords: ["sleep problem", "insomnia", "can't sleep", "sleeping issue"], title: "Sleep Problem" },

      // Fitness
      { keywords: ["weight loss", "lose weight"], title: "Weight Loss" },
      { keywords: ["weight gain", "gain weight"], title: "Weight Gain" },
      { keywords: ["workout", "exercise", "gym"], title: "Fitness" },
      { keywords: ["diet", "nutrition", "food"], title: "Diet" },

      // Medical
      { keywords: ["report", "lab report", "test report", "blood test"], title: "Medical Report" },
      { keywords: ["prescription"], title: "Prescription" },
      { keywords: ["x-ray", "xray", "mri", "ct scan", "scan"], title: "Scan Report" },

      // General
      { keywords: ["doctor", "consultation", "appointment"], title: "Doctor Consultation" },
      { keywords: ["breathing", "breathless", "shortness of breath"], title: "Breathing Issue" },
      { keywords: ["fatigue", "tired", "weakness", "weak"], title: "Fatigue" },
      { keywords: ["dizziness", "dizzy", "vertigo"], title: "Dizziness" },
    ];

    // Search through all user messages for specific topics
    for (const msg of msgs) {
      if (msg.from === "user" && msg.text) {
        const text = msg.text.toLowerCase();

        // Skip if it's just a greeting
        if (greetings.includes(text.trim())) continue;

        // Check for specific topics
        for (const topic of specificTopics) {
          if (topic.keywords.some(keyword => text.includes(keyword))) {
            return topic.title;
          }
        }
      }
    }

    // Find first meaningful non-greeting message
    const meaningfulMsg = msgs.find((m) => {
      if (m.from !== "user" || !m.text) return false;
      const text = m.text.trim().toLowerCase();
      return text.length > 3 && !greetings.includes(text);
    });

    if (meaningfulMsg?.text) {
      let title = meaningfulMsg.text.trim();
      // Capitalize each word
      title = title.split(' ').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      return title.length > 25 ? title.slice(0, 25) + "…" : title;
    }

    return chatTitles[chatId] || "New Chat";
  };

  return (
    <aside className={`sidebar ${isOpen ? "" : "sidebar--closed"}`}>
      <div className="sidebar-top">
        <img src={LogoIcon} alt="Fitnova" className="sidebar-logo" />
        {isOpen && (
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={onCloseSidebar}
            aria-label="Close sidebar"
            title="Close sidebar"
          >
            {/* <svg width="20" height="20" viewBox="0 0 24 24" fill="none" src={sidebar} aria-hidden className="sidebar-close-icon">
              <rect x="4" y="6" width="3" height="12" rx="1" fill="currentColor"/>
              <path d="M14 8l-4 4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg> */}
            <img src={sidebar} alt="" className="sidebar-close-icon" height={20} width={20} />
          </button>
        )}
      </div>

      <button
        type="button"
        className="chat-history-btn"
        onClick={onChatHistoryClick}
        aria-expanded={!collapsed}
        aria-label={collapsed ? "Expand chat history" : "Collapse chat history"}
      >
        <img src={ChatBubbleIcon} alt="" aria-hidden />
        <span>Chat History</span>
        <img
          src={ChevronRightIcon}
          alt=""
          className="chevron-icon"
          style={{ transform: collapsed ? "rotate(90deg)" : "none" }}
          aria-hidden
        />
      </button>

      {hasRecentChats && collapsed && (
        <>
          <div className="sidebar-recent-heading">Recent Conversations</div>
          <div className="chat-history-list">
            {(() => {
              // Order chat IDs: pinned first (original order preserved), then others (original order preserved)
              const pinnedSet = new Set(pinnedChats);
              const ordered = [
                ...chatIds.filter((id) => pinnedSet.has(id)),
                ...chatIds.filter((id) => !pinnedSet.has(id)),
              ];
              return ordered.map((chatId) => (
                <div
                  key={chatId}
                  className={`chat-history-item ${chatId === activeChatId ? "active" : ""
                    }`}
                  onClick={() => onSelectChat(chatId)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                >
                  <div style={{ display: "flex", alignItems: "center", flex: 1, overflow: "hidden" }}>
                    <img
                      src={ChatBubbleIcon}
                      alt=""
                      className="item-chat-icon"
                      aria-hidden
                    />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {getChatTitle(chatId)}
                      {pinnedChats.includes(chatId) && (
                        <i className="fa-solid fa-thumbtack chat-pinned-icon" aria-hidden style={{ color: '#0f52ba', marginLeft: 6 }} />
                      )}
                    </span>
                  </div>
                  <div style={{ position: "relative" }} ref={openMenuId === chatId ? menuRef : null}>
                    <button
                      type="button"
                      onClick={(e) => handleMenuClick(e, chatId)}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px 8px",
                        fontSize: "18px",
                        color: "#999",
                        borderRadius: "4px",
                        marginLeft: "8px",
                        flexShrink: 0,
                        lineHeight: 1,
                      }}
                      onMouseEnter={(e) => (e.target.style.color = "#666")}
                      onMouseLeave={(e) => (e.target.style.color = "#999")}
                      title="Options"
                      aria-label="Options"
                    >
                      ⋮
                    </button>

                    {openMenuId === chatId && (
                      <div
                        style={{
                          position: "absolute",
                          right: 0,
                          top: "100%",
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          padding: "2px 0",
                          zIndex: 1000,
                          minWidth: "120px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, chatId)}
                          aria-label="Delete chat"
                          title="Delete chat"
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            color: "#dc3545",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fee2e2")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <i className="fa-solid fa-trash" style={{ color: '#f70202', fontSize: '16px' }} aria-hidden={true} />
                          <span style={{ flex: 1, textAlign: 'left', color: '#b91c1c' }}>Delete Chat</span>
                        </button>

                        <button
                          type="button"
                          onClick={(e) => handlePinToggle(e, chatId)}
                          aria-label={pinnedChats.includes(chatId) ? 'Unpin chat' : 'Pin chat'}
                          title={pinnedChats.includes(chatId) ? 'Unpin chat' : 'Pin chat'}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            color: "#111",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <i className={`fa-solid fa-thumbtack`} style={{ color: pinnedChats.includes(chatId) ? '#0f52ba' : '#6b7280', fontSize: '14px', transform: pinnedChats.includes(chatId) ? 'rotate(0deg)' : 'rotate(-20deg)' }} aria-hidden={true} />
                          <span style={{ flex: 1, textAlign: 'left' }}>{pinnedChats.includes(chatId) ? 'Unpin Chat' : 'Pin Chat'}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>
        </>
      )}
    </aside>
  );
}

export default Sidebar;